import type { NavContext } from './navigator.js'
import { getNavContext } from './navigator.js'

export interface UAHighEntropyValues {
  architecture?: string
  bitness?: string
  model?: string
  platformVersion?: string
  fullVersionList?: Array<{ brand: string; version: string }>
}

export interface EnvContext extends NavContext {
  webglRenderer?: string
  webglVendor?: string
  webglMaxTextureSize?: number
  webglFragPrecision?: number
  webglCompressedFormats?: {
    s3tc: boolean   // DXT/BC — desktop GPU
    pvrtc: boolean  // PowerVR — iOS only
    etc2: boolean   // GLES 3.0+ — Android mainstream
    astc: boolean   // Adreno 4xx+ / Mali Txx+ — modern mobile
  }
  hardwareConcurrency?: number
  deviceMemory?: number
  devicePixelRatio?: number
  screenWidth?: number
  screenHeight?: number
  safeAreaInsetTop?: number
  audioSampleRate?: number
  hasVibration?: boolean
  hasDeviceMotion?: boolean
  pointerType?: 'coarse' | 'fine' | 'none'
  hoverCapability?: boolean
  fontProbes?: Record<string, boolean>
  highEntropyData?: UAHighEntropyValues
  windowsVersion?: string | null
  hasBrave?: boolean
  /** true when running on Safari/WebKit 26+ (iOS 26 / macOS 26), detected via CSS feature probe. */
  isIOS26Plus?: boolean
  /** true when window.__$_qihoo360_$__ properties are present — 360 Mobile Browser signal. */
  has360Mobile?: boolean
}

// OS-specific system fonts used to cross-confirm UA-based OS detection.
// Only ~5 fonts; each measureText() call is < 1ms total overhead < 5ms.
const OS_FONTS = [
  '.AppleSystemUIFont',
  'Segoe UI',
  'Noto Color Emoji',
  'Ubuntu',
  'HarmonyOS Sans',
]

function probeFonts(): Record<string, boolean> {
  try {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return {}
    const TEST = 'mmmmmmmmmmlli'
    ctx.font = '72px monospace'
    const baseline = ctx.measureText(TEST).width
    const result: Record<string, boolean> = {}
    for (const font of OS_FONTS) {
      ctx.font = `72px '${font}', monospace`
      result[font] = ctx.measureText(TEST).width !== baseline
    }
    return result
  } catch {
    return {}
  }
}

function getWebGLInfo(): {
  renderer?: string
  vendor?: string
  maxTextureSize?: number
  fragPrecision?: number
  compressedFormats?: { s3tc: boolean; pvrtc: boolean; etc2: boolean; astc: boolean }
} {
  try {
    const canvas = document.createElement('canvas')
    const gl = (canvas.getContext('webgl') ?? canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null
    if (!gl) return {}
    const ext = gl.getExtension('WEBGL_debug_renderer_info')
    const result: ReturnType<typeof getWebGLInfo> = {
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) as number,
      fragPrecision:  gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT)?.rangeMax,
      compressedFormats: {
        s3tc:  !!gl.getExtension('WEBGL_compressed_texture_s3tc'),
        pvrtc: !!gl.getExtension('WEBGL_compressed_texture_pvrtc'),
        etc2:  !!gl.getExtension('WEBGL_compressed_texture_etc'),
        astc:  !!gl.getExtension('WEBGL_compressed_texture_astc_ldr'),
      },
    }
    if (ext) {
      result.renderer = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string
      result.vendor   = gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) as string
    }
    return result
  } catch {
    return {}
  }
}

// Probe CSS env(safe-area-inset-top). On iOS devices with notch / Dynamic Island
// this returns a hardware-defined pixel value (47–59px). Desktop always returns 0.
// The value originates from the OS rendering pipeline — it cannot be overridden via JS.
function getSafeAreaInsetTop(): number {
  try {
    const el = document.createElement('div')
    el.style.cssText = 'position:fixed;top:env(safe-area-inset-top,0px);visibility:hidden;pointer-events:none'
    document.body.appendChild(el)
    const top = parseFloat(getComputedStyle(el).top)
    document.body.removeChild(el)
    return isNaN(top) ? 0 : top
  } catch {
    return 0
  }
}

function getAudioSampleRate(): number | undefined {
  try {
    const AC = window.AudioContext ?? (window as any).webkitAudioContext
    if (!AC) return undefined
    const ac = new AC() as AudioContext
    const rate = ac.sampleRate
    void ac.close()
    return rate
  } catch {
    return undefined
  }
}

function getPointerType(): 'coarse' | 'fine' | 'none' | undefined {
  try {
    if (window.matchMedia('(pointer: coarse)').matches) return 'coarse'
    if (window.matchMedia('(pointer: fine)').matches) return 'fine'
    if (window.matchMedia('(pointer: none)').matches) return 'none'
  } catch {
    // matchMedia unavailable
  }
  return undefined
}

function getHoverCapability(): boolean | undefined {
  try {
    return window.matchMedia('(hover: hover)').matches
  } catch {
    return undefined
  }
}

function deriveWindowsVersion(platformVersion: string | undefined): string | null {
  if (!platformVersion) return null
  const major = parseInt(platformVersion.split('.')[0] ?? '0', 10)
  return isNaN(major) ? null : major >= 13 ? '11' : '10'
}

/**
 * Collect all available browser signals into a single EnvContext.
 * Safe to call in Node.js/SSR — missing APIs degrade gracefully.
 * Await this once at app start, then pass the result to parseUA() as `options.ctx`.
 */
export async function getEnvContext(): Promise<EnvContext> {
  const base = getNavContext()

  const ctx: EnvContext = {
    userAgent: base.userAgent,
    platform: base.platform,
    language: base.language,
    maxTouchPoints: base.maxTouchPoints,
  }
  if (base.browserLanguage !== undefined) ctx.browserLanguage = base.browserLanguage
  if (base.mimeTypes !== undefined) ctx.mimeTypes = base.mimeTypes
  if (base.connection !== undefined) ctx.connection = base.connection
  if (base.userAgentData !== undefined) ctx.userAgentData = base.userAgentData

  if (typeof navigator !== 'undefined') {
    ctx.hardwareConcurrency = navigator.hardwareConcurrency
    ctx.deviceMemory = navigator.deviceMemory
  }

  if (typeof CSS !== 'undefined') {
    ctx.isIOS26Plus = CSS.supports('animation-timeline', 'view()')
  }

  if (typeof window !== 'undefined') {
    const w = window as unknown as Record<string, unknown>
    ctx.has360Mobile = 'window__$_qihoo360_$__dayMode' in w || 'window__$_qihoo360_$__nightMode' in w
  }

  if (typeof window !== 'undefined') {
    ctx.devicePixelRatio  = window.devicePixelRatio
    ctx.screenWidth       = window.screen?.width
    ctx.screenHeight      = window.screen?.height
    ctx.safeAreaInsetTop  = getSafeAreaInsetTop()
    ctx.pointerType       = getPointerType()
    ctx.hoverCapability = getHoverCapability()
    ctx.fontProbes      = probeFonts()
    ctx.audioSampleRate = getAudioSampleRate()
    ctx.hasVibration    = 'vibrate' in navigator
    ctx.hasDeviceMotion = 'DeviceMotionEvent' in window
    try {
      ctx.hasBrave = await (navigator as unknown as { brave?: { isBrave?(): Promise<boolean> } }).brave?.isBrave?.() ?? false
    } catch {
      ctx.hasBrave = false
    }
    const { renderer, vendor, maxTextureSize, fragPrecision, compressedFormats } = getWebGLInfo()
    if (renderer !== undefined)          ctx.webglRenderer = renderer
    if (vendor !== undefined)            ctx.webglVendor = vendor
    if (maxTextureSize !== undefined)    ctx.webglMaxTextureSize = maxTextureSize
    if (fragPrecision !== undefined)     ctx.webglFragPrecision = fragPrecision
    if (compressedFormats !== undefined) ctx.webglCompressedFormats = compressedFormats
  }

  if (base.userAgentData) {
    try {
      const data = await base.userAgentData.getHighEntropyValues([
        'architecture', 'bitness', 'model', 'platformVersion', 'fullVersionList',
      ])
      ctx.highEntropyData = {
        architecture: data['architecture'],
        bitness: data['bitness'],
        model: data['model'],
        platformVersion: data['platformVersion'],
        fullVersionList: data['fullVersionList'] as unknown as Array<{ brand: string; version: string }>,
      }
      if (base.userAgentData.platform === 'Windows') {
        ctx.windowsVersion = deriveWindowsVersion(data['platformVersion'])
      }
    } catch {
      // High entropy values unavailable (non-Chrome, permission denied, etc.)
    }
  }

  return ctx
}

/**
 * Probe the iOS/macOS Safari minor version within the 26.x series via CSS/JS feature detection.
 *
 * Returns the detected version string ('26.0'–'26.5') or null if not running on Safari 26+.
 * Detection is ordered from latest to earliest; the first matching check wins.
 * Intended as a supplement to UA parsing when the UA string is frozen at iOS 18.x.
 *
 * Feature map (each check is first available in that version):
 *   26.5 — Origin API
 *   26.4 — PerformanceResourceTiming.finalResponseHeadersStart
 *   26.3 — NavigateEvent.prototype.signal
 *   26.2 — Math.sumPrecise
 *   26.0 — CSS animation-timeline: view() (Scroll-driven Animations)
 */
export function probeIOS26Version(): string | null {
  try {
    if (typeof CSS === 'undefined' || !CSS.supports('animation-timeline', 'view()')) return null

    if (typeof (globalThis as Record<string, unknown>)['Origin'] !== 'undefined') return '26.5'

    if (
      typeof PerformanceResourceTiming !== 'undefined' &&
      'finalResponseHeadersStart' in PerformanceResourceTiming.prototype
    ) return '26.4'

    const NavigateEvent = (globalThis as Record<string, unknown>)['NavigateEvent'] as { prototype?: Record<string, unknown> } | null | undefined
    if (NavigateEvent?.prototype != null && 'signal' in NavigateEvent.prototype) return '26.3'

    if (typeof (Math as unknown as Record<string, unknown>)['sumPrecise'] === 'function') return '26.2'

    return '26.0'
  } catch {
    return null
  }
}
